/**
 * The Season Pass, as every upgrade surface describes it: /pricing's Season
 * Pass card and the credits upgrade modal's read these, so the two cannot
 * drift (the modal once promised "Unlimited AI chat" against a 120-a-day
 * cap and "Priority support" nothing on /pricing offered, at a hard-coded
 * $99 that would have been wrong from October 1).
 *
 * The PRICE is not here: it follows the season clock in seasonPricing.js
 * (seasonPassPriceUsd / seasonDeadlineLine), the same clock the server
 * charges by. `**x**` / `__x__` mark key words; render with
 * components/PlanFeatureText.jsx.
 */
import { BUILD_NAMES } from '../content/buildNames';

// The Pass's internal allowance. Founder, 2026-10-18 (#4668): students never
// see it ("season pass covers everything without any mention of any usage or
// credits"), so nothing student-facing renders this; the ledger and the ops
// alert read it from the server. Kept here only for the drift pin against
// payment_manager_v2's constant.
export const SEASON_PASS_CREDITS = 150;

// The one line that sells the tier (#4668): what the Pass covers, never a
// count. "All season" is the promise; the daily Astra cap and heavy-use
// limits are fair use in the Terms, not a feature line.
export const SEASON_PASS_COVERS_LINE = '**Everything, all season**: every college you add with its full read, every Major Map and Strategy, every essay outline and review';

// The refund promise, one definition for every surface that makes it
// (/pricing and the upgrade modal through seasonPassFeatures, the cancel
// modal, the Terms). Founder 2026-10-18 ~2:20 PM PT (#4718, ADR 0055): "Full
// refund only if they have just used one college.. anything more than that no
// refund." The condition travels with the promise wherever it is shown (FTC
// 16 CFR 239), in product words: a "full read" is what the covers line sells,
// never the internal ledger (ADR 0053). The 30 days and the one college are
// payment_manager_v2/refund_policy.py's REFUND_WINDOW_DAYS and
// MAX_COLLEGES_FOR_REFUND, pinned by test_refund_policy.py.
export const REFUND_POLICY_LEAD = 'Full refund';
export const REFUND_POLICY_CONDITION = "within 30 days if you've had a full read on no more than one college";
export const REFUND_POLICY_LINE = `${REFUND_POLICY_LEAD} ${REFUND_POLICY_CONDITION}`;

// The pricing page's counselor hint (#4972, founder 2026-10-20: "also a hint
// about this in the pricing page"). One line near the Season Pass card on
// both /pricing layouts. Counselors are partners: the Pass works alongside
// one, never instead of one. "A private link" is Sharing as it is today
// (a read-only link to the sections the student picks, share_gateway).
export const SEASON_PASS_COUNSELOR_LINE = 'Working with a counselor? The Season Pass works alongside them. Arrive with your list, activities, programs and essay ideas already mapped out, share your work through a private link, and your time together goes further.';

export const SEASON_PASS_DESCRIPTION = 'Everything Stratia does, for the whole application season.';

// Founder 2026-08-04: sell the SEASON, not arithmetic: "you can complete the
// full season with any number of colleges.. no need to talk about refreshes."
export const SEASON_PASS_BEST_FOR = 'Enough for your entire season: your full list, your essays, your Major Map, from first search to final decision.';

// What Build gives a Pass holder (founder 2026-10-01: "the season pass now
// includes everything under build"; and "Brainstorm is not a feature.. it is
// a prereq for the 3 features"). So the line names the three steps as the
// features and Brainstorm only as the step that comes first. An ORDER, not a
// source: File a patent and Practice a pitch read the seven answers, but Write
// a paper keeps its own seven (study.py), so the line never says the three are
// built from it (review 2026-10-01). One application is the
// founder's 2026-09-22 promise for File a patent; since #3864 (founder pick A,
// 2026-10-11) it is its own line below this one, with the package detail in
// an expander, because nothing patent-related is sold separately.
export function seasonPassBuildLine() {
    const steps = [
        `**${BUILD_NAMES.steps.patent.label}**`,
        `**${BUILD_NAMES.steps.research.label}** with a mentor`,
        `**${BUILD_NAMES.steps.pitch.label}** out loud`,
    ];
    const listed = `${steps.slice(0, -1).join(', ')}, and ${steps[steps.length - 1]}`;
    return `**Build something real**: a ${BUILD_NAMES.home.label} first, seven questions in your words, then ${listed}`;
}

// The patent application the Pass includes (founder 2026-09-22: one per Pass;
// #3864, mock A: it lives on the Pass card, not on an Add-ons card, because
// it is not sold on its own). The package lines are what the old card listed.
export const SEASON_PASS_PATENT_LINE = `**${BUILD_NAMES.steps.patent.label}:** one provisional application included`;
export const SEASON_PASS_PATENT_PACKAGE = [
    'The specification and drawings, numbered, claimed, checked against each other',
    'The two files a patent office takes, ready to file',
    'Your prior-art table, and the free published-disclosure route beside it',
    "You file, in your family's name; the USPTO fee is paid to the USPTO",
];

// A pass bought as one payment (the rupee Season Pass, #4966; every new
// Season Pass behind one_payment_passes, #4986) has nothing to cancel; its
// last line says so instead of "Cancel anytime".
export const ONE_PAYMENT_LINE = 'One payment for the year, no renewal';

// The one-payment passes on the Season Pass card (#4986; founder pick B on
// the Desk, 2026-10-21: one card, and for grades 9 to 12 (founder 2026-10-23:
// "9 to 12 not 9 to 11") a switch at its top,
// "This season" first). The words of each state, one definition for /pricing,
// the upgrade modal and the trial wall.
export const PASS_TERMS = Object.freeze({
    season: Object.freeze({ label: 'This season', unit: 'one payment, for one year' }),
    // `covers`: the mock's own line for the second state, in place of the
    // Season Pass's "Everything, all season" (one season is not the promise).
    graduation: Object.freeze({ label: 'Through graduation', unit: 'one payment',
        covers: '**Everything in the Season Pass, every year**' }),
});
export const SEASON_PASS_NO_RENEWAL_LINE = 'It does not renew. Nothing to cancel.';
/** How long Through Graduation runs, in words (#5239, founder 2026-10-23:
 * "give at least a year"): the later of June 30 of the graduation year and a
 * year from purchase, the server's pass_terms.term_end. One phrase for every
 * line that states the rule without a date (pinned by test_pass_terms.py). */
export const THROUGH_GRADUATION_TERM_WORDS =
    'through June 30 of your graduation year, or a year from the day you buy it if that is later';
/** The server's end date as the page says it: the Pacific calendar day (the
 * server's end is the last second of a Pacific day; a browser elsewhere
 * would otherwise show the next day). '' when unreadable. */
export function passEndDateLabel(iso) {
    const at = iso ? new Date(iso) : null;
    if (!at || Number.isNaN(at.getTime())) return '';
    return at.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'long', day: 'numeric' });
}
/** Through Graduation's promise, with the server's own end date (#5239: a
 * senior's runs a year from purchase, past their June). */
export const throughGraduationLine = (endsAt) =>
    `Every season until your applications are done, through ${passEndDateLabel(endsAt)}. It never renews.`;
export const seasonPassBuyLabel = (priceLabel) => `Get the Season Pass: ${priceLabel}`;
export const throughGraduationBuyLabel = (priceLabel) => `Get Through Graduation: ${priceLabel}`;
/** Through Graduation's promise before the server knows the student (#5207:
 * signed out, or no grade on file): who it is for, in place of the year the
 * server works out from the profile after sign-in. */
export const THROUGH_GRADUATION_GRADES_LINE =
    `For students in grades 9 to 12. Every season until your applications are done, ${THROUGH_GRADUATION_TERM_WORDS}. It never renews.`;
/** The graduation-year ask when checkout found no grade on file (#5207),
 * worded for /pricing on the roadmap's own prompt (GraduationYearPrompt). */
export const THROUGH_GRADUATION_YEAR_ASK = Object.freeze({
    title: 'What year do you graduate high school?',
    lead: `Through Graduation is for students in grades 9 to 12 and runs ${THROUGH_GRADUATION_TERM_WORDS}. Save your year and checkout opens if it fits.`,
    save: 'Save and continue',
});

/** /pricing's "How long does it last?" answer, true for what this visitor
 * is sold: the renewing Pass, the rupee pass, or the one-payment Pass with
 * (for grades 9 to 12) Through Graduation beside it. */
export function passLengthAnswer({ renews = true, local = false, graduation = false } = {}) {
    if (renews) {
        return 'A year from the day you buy it: the whole application season. It renews each year unless you cancel, and you can cancel anytime. Anything you bought on top of the Pass stays yours through a renewal.';
    }
    if (local) {
        return 'A year from the day you buy it: the whole application season. Paid in rupees, it is one payment and does not renew, so there is nothing to cancel.';
    }
    const base = 'A year from the day you buy it: the whole application season. It is one payment and does not renew, so there is nothing to cancel.';
    return graduation
        ? `${base} Through Graduation is one payment too, and runs ${THROUGH_GRADUATION_TERM_WORDS}.`
        : base;
}

/** /pricing's "What is Through Graduation?" answer (#4986, product spec
 * s.10): who it is for, how long it runs, and the shared refund rule from
 * its one constant. Shown only to a student the server offers it to. */
export function throughGraduationFaqAnswer(priceLabel) {
    return `The Season Pass for every season until your applications are done, for students in 9th to 12th grade: ${priceLabel}, one payment, ${THROUGH_GRADUATION_TERM_WORDS}. It never renews, so there is nothing to cancel. ${REFUND_POLICY_LINE}, the same as the Season Pass.`;
}

// A Season Pass holder's upgrade to Through Graduation for the difference
// (#5228, founder 2026-10-23: "only charge them the remaining.. like we would
// on a upgrade"). The figures are the server's: what the pass was paid, read
// from the account's own record, and the difference it charges.
export const throughGraduationUpgradeLine = (priceLabel, paidLabel) =>
    `Upgrade: ${priceLabel} (you paid ${paidLabel} for this season).`;
export const throughGraduationUpgradeBuyLabel = (priceLabel) => `Upgrade to Through Graduation: ${priceLabel}`;
export const THROUGH_GRADUATION_UPGRADE_SMALL_PRINT =
    'One student, every season through graduation; fair use applies. Your Season Pass is replaced from the day you upgrade.';
/** A holder whose Season Pass already runs as long as Through Graduation
 * would (rare since #5239's year floor: the server decides from the two
 * dates): what the upgrade would give them, which is nothing, said plainly
 * with the date. */
export const throughGraduationCoversNote = (passEndsLabel) =>
    `Your Season Pass already runs through ${passEndsLabel}, as long as Through Graduation would. There is nothing to upgrade to.`;
/** A senior whose Season Pass already runs to graduation (#5246, founder
 * 2026-10-23, option B; narrowed the same day, #5251, "ok": a senior whose
 * pass ends BEFORE graduation is offered the upgrade). The server refuses
 * `senior` only when the pass's end is on or after June 30 of the class
 * year, so "covers you through graduation" is true for every senior who
 * reads it; the pass's own date when the server sent one. */
export const SENIOR_UPGRADE_LINE = 'Your Season Pass already covers you through graduation, so there is nothing to upgrade to.';
export const seniorUpgradeNote = (passEndsLabel) => (passEndsLabel
    ? `Your Season Pass already covers you through graduation: it runs through ${passEndsLabel}. There is nothing to upgrade to.`
    : SENIOR_UPGRADE_LINE);
export const SUPPORT_UPGRADE_LINE =
    'We could not work out your upgrade from your Season Pass. Write to support@stratiaadmissions.com and we will sort it out.';

// A comped student's Through graduation state (#5015, founder 2026-10-21:
// "allow"; Through Graduation covers the comp, which ends at purchase).
export const THROUGH_GRADUATION_COMP_LINE = 'Your complimentary Season Pass is included: Through Graduation takes its place.';

/** What a page says when checkout refuses Through Graduation (#4986): what
 * happened, then what to do, by the server's own reason
 * (payment_manager_v2 pass_terms NOT_OFFERED_REASONS). Null: not a refusal
 * this explains. One definition for /pricing and the trial wall. */
export function throughGraduationRefusal(result) {
    if (!result?.error) return null;
    if (result.error === 'offer_changed') {
        return 'Your graduation year changed since this page loaded. Reload the page to see the right dates.';
    }
    if (result.error === 'plan_active') return 'You already have a plan. Your plan page has its options.';
    if (result.error !== 'not_offered') return null;
    if (result.reason === 'unknown_grade' || result.reason === 'grade_year_mismatch') {
        return 'Check your grade and graduation year in your profile, then try again. The Season Pass covers this season either way.';
    }
    // A holder's upgrade (#5228), by the server's reason (pass_upgrade.UPGRADE_REASONS).
    if (result.reason === 'season_pass_covers') {
        return 'Your Season Pass already runs as long as Through Graduation would. There is nothing to upgrade to.';
    }
    if (result.reason === 'senior') return SENIOR_UPGRADE_LINE;
    if (result.reason === 'paid_by_parent') {
        return 'This Season Pass was paid by a parent. Write to support@stratiaadmissions.com to change it.';
    }
    if (result.reason === 'already_through_graduation') return 'You already have Through Graduation.';
    if (['paid_unknown', 'currency_unpriced', 'nothing_to_pay'].includes(result.reason)) return SUPPORT_UPGRADE_LINE;
    return 'Through Graduation is for students in 9th to 12th grade who have not graduated yet. The Season Pass covers this season.';
}

// The date the one-payment rules began, as the Terms state it (#4986:
// "existing purchases keep their rules"). A Season Pass bought before it as
// a subscription keeps renewing on its own terms. SET THIS TO THE DAY
// one_payment_passes GOES ON FOR EVERYONE (the launch PR); until then only
// the Just us preview reads it.
export const ONE_PAYMENT_RULES_FROM_LABEL = 'October 21, 2026';

/** The Season Pass lines, in order. `highlight` marks the one line that
 * sells the tier: what it covers, never a count (#4668). `renews` false: the
 * one-payment pass (useSeasonPassPrice's price.renews). `throughGraduation`:
 * the switch's second state (#4986). */
export function seasonPassFeatures({ renews = true, throughGraduation = false } = {}) {
    const last = throughGraduation
        ? [{ text: PASS_TERMS.graduation.covers }]
        : [{ text: renews ? 'Cancel anytime' : ONE_PAYMENT_LINE }];
    return [
        { text: '__Everything in Free__' },
        { text: SEASON_PASS_COVERS_LINE, highlight: true },
        { text: '**Astra**, for questions any time, all season' },
        { text: seasonPassBuildLine() },
        { text: SEASON_PASS_PATENT_LINE, details: SEASON_PASS_PATENT_PACKAGE },
        // #3849: analyses are not silently kept current; they say when a
        // refresh is due (fit_staleness: profile and KB changes).
        { text: '**Tells you** when an analysis needs a refresh' },
        // The refund promise and its condition, wherever the Pass is
        // advertised (#4718; the Terms, s.7, state the rule in full).
        { text: `**${REFUND_POLICY_LEAD}** ${REFUND_POLICY_CONDITION}` },
        // Through Graduation (#4986) says its term in its own line
        // (throughGraduationLine); its last line is what it covers.
        ...last,
    ];
}
